import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Trash2, Pencil, Plus, ExternalLink } from "lucide-react";
import AdminDialog from "../../components/admin/AdminDialog";
import SingleImageField from "../../components/common/SingleImageField";

const slugify = (s) =>
  (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const blankCampaign = {
  slug: "",
  title: "",
  subtitle: "",
  meta_title: "",
  meta_description: "",
  meta_keywords: [],
  og_image_url: "",
  hero_image_url: "",
  mobile_hero_image_url: "",
  hero_eyebrow: "",
  hero_cta_label: "",
  hero_cta_url: "",
  intro_body: "",
  sections: [],
  start_at: "",
  end_at: "",
  is_active: true,
  order: 0,
};

export default function AdminCampaigns() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [form, setForm] = useState(blankCampaign);
  const [sectionsJson, setSectionsJson] = useState("[]");
  const [keywordsStr, setKeywordsStr] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/admin/campaigns");
      setRows(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load campaigns");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditingId(null);
    setForm(blankCampaign);
    setSectionsJson("[]");
    setKeywordsStr("");
    setDialogOpen(true);
  };

  const openEdit = (c) => {
    setEditingId(c.id);
    setForm({
      ...blankCampaign,
      ...c,
      start_at: c.start_at || "",
      end_at: c.end_at || "",
    });
    setSectionsJson(JSON.stringify(c.sections || [], null, 2));
    setKeywordsStr((c.meta_keywords || []).join(", "));
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.title) return toast.error("Title required");
    if (!form.slug) return toast.error("Slug required");
    if (form.start_at && form.end_at && form.end_at < form.start_at) {
      return toast.error("End date must be after start date");
    }
    let sections;
    try {
      sections = JSON.parse(sectionsJson || "[]");
      if (!Array.isArray(sections)) throw new Error();
    } catch {
      return toast.error("Sections must be a JSON array");
    }
    const body = {
      ...form,
      slug: slugify(form.slug || form.title),
      sections,
      order: Number(form.order) || 0,
      meta_keywords: keywordsStr
        .split(",")
        .map((k) => k.trim())
        .filter(Boolean),
    };
    // Omit empty date strings
    if (!body.start_at) delete body.start_at;
    if (!body.end_at) delete body.end_at;

    try {
      if (editingId) await api.put(`/admin/campaigns/${editingId}`, body);
      else await api.post("/admin/campaigns", body);
      toast.success(editingId ? "Updated" : "Created");
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Archive this campaign?")) return;
    try {
      await api.delete(`/admin/campaigns/${id}`);
      toast.success("Archived");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div data-testid="admin-campaigns-page">
      <div className="flex justify-between items-start mb-6 flex-wrap gap-3">
        <div>
          <div className="overline mb-2">Marketing</div>
          <h1 className="font-display text-4xl">Campaign Landing Pages</h1>
          <p className="text-sm text-[hsl(var(--kb-muted))] mt-1">
            SEO-optimised URLs (e.g. /campaign/bridal-edit) with custom meta tags,
            hero, and curated content blocks.
          </p>
        </div>
        <button className="kb-btn-primary" onClick={openNew} data-testid="campaign-add-btn">
          <Plus size={16} className="inline mr-1" /> New Campaign
        </button>
      </div>

      <div className="bg-white border border-[hsl(var(--kb-border))] overflow-x-auto">
        {loading ? (
          <div className="p-8 text-center text-[hsl(var(--kb-muted))]" data-testid="campaigns-loading">
            Loading…
          </div>
        ) : error ? (
          <div className="p-8 text-center text-red-700" data-testid="campaigns-error">
            {error}
          </div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-[hsl(var(--kb-muted))]">
            No campaigns yet — create your first one.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[hsl(var(--kb-surface-alt))] text-left text-[hsl(var(--kb-muted))]">
              <tr>
                <th className="p-3">Order</th>
                <th>Title</th>
                <th>Slug</th>
                <th>Schedule</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr
                  key={c.id}
                  className="border-t border-[hsl(var(--kb-border))]"
                  data-testid={`campaign-row-${c.slug}`}
                >
                  <td className="p-3 font-mono">{c.order}</td>
                  <td className="p-3 font-medium">
                    <div>{c.title}</div>
                    {c.subtitle && (
                      <div className="text-xs text-[hsl(var(--kb-muted))] line-clamp-1">
                        {c.subtitle}
                      </div>
                    )}
                  </td>
                  <td className="p-3 text-xs font-mono">/{c.slug}</td>
                  <td className="p-3 text-xs">
                    {c.start_at ? c.start_at.slice(0, 10) : "—"} →{" "}
                    {c.end_at ? c.end_at.slice(0, 10) : "∞"}
                  </td>
                  <td className="p-3 text-xs uppercase">
                    {c.is_active ? "active" : "inactive"}
                  </td>
                  <td className="p-3">
                    <div className="flex gap-2">
                      <a
                        href={`/campaign/${c.slug}`}
                        target="_blank"
                        rel="noreferrer"
                        className="kb-btn-outline !py-1 !px-2 !text-[10px]"
                        data-testid={`campaign-view-${c.slug}`}
                      >
                        <ExternalLink size={12} />
                      </a>
                      <button
                        onClick={() => openEdit(c)}
                        className="kb-btn-outline !py-1 !px-2 !text-[10px]"
                        data-testid={`campaign-edit-${c.slug}`}
                      >
                        <Pencil size={12} />
                      </button>
                      <button
                        onClick={() => remove(c.id)}
                        className="text-red-700 p-1"
                        data-testid={`campaign-del-${c.slug}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <AdminDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title="Campaign Landing Page"
        subtitle={editingId ? "Edit" : "New"}
        maxWidth="max-w-3xl"
        testid="campaign-dialog"
        footer={
          <>
            <button className="kb-btn-outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </button>
            <button
              className="kb-btn-primary"
              onClick={save}
              data-testid="campaign-save-btn"
            >
              {editingId ? "Update" : "Create"}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="grid md:grid-cols-2 gap-3">
            <input
              className="kb-input"
              placeholder="Title (e.g. The Bridal Edit)"
              value={form.title}
              onChange={(e) =>
                setForm({
                  ...form,
                  title: e.target.value,
                  slug: form.slug || slugify(e.target.value),
                })
              }
              data-testid="campaign-title"
            />
            <input
              className="kb-input"
              placeholder="Slug (e.g. bridal-edit)"
              value={form.slug}
              onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })}
              data-testid="campaign-slug"
            />
          </div>
          <input
            className="kb-input"
            placeholder="Subtitle"
            value={form.subtitle}
            onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
          />

          <div className="grid md:grid-cols-3 gap-3">
            <SingleImageField
              label="Hero image"
              value={form.hero_image_url}
              onChange={(key) => setForm({ ...form, hero_image_url: key || "" })}
              aspect="aspect-[16/9]"
              testid="campaign-hero"
            />
            <SingleImageField
              label="Mobile hero (optional)"
              value={form.mobile_hero_image_url}
              onChange={(key) => setForm({ ...form, mobile_hero_image_url: key || "" })}
              aspect="aspect-[4/5]"
              testid="campaign-hero-mobile"
            />
            <SingleImageField
              label="OG / share image"
              value={form.og_image_url}
              onChange={(key) => setForm({ ...form, og_image_url: key || "" })}
              hint="Used when shared on WhatsApp / Twitter"
              aspect="aspect-[1.91/1]"
              testid="campaign-og"
            />
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <input
              className="kb-input"
              placeholder="Hero Eyebrow (e.g. Autumn-Winter)"
              value={form.hero_eyebrow}
              onChange={(e) =>
                setForm({ ...form, hero_eyebrow: e.target.value })
              }
            />
            <input
              className="kb-input"
              placeholder="Hero CTA Label (e.g. Shop the Edit)"
              value={form.hero_cta_label}
              onChange={(e) =>
                setForm({ ...form, hero_cta_label: e.target.value })
              }
            />
            <input
              className="kb-input"
              placeholder="Hero CTA URL (e.g. /shop?occasion=Bridal)"
              value={form.hero_cta_url}
              onChange={(e) =>
                setForm({ ...form, hero_cta_url: e.target.value })
              }
            />
          </div>

          <textarea
            rows={3}
            className="kb-input"
            placeholder="Intro body (long-form SEO copy shown under hero)"
            value={form.intro_body}
            onChange={(e) => setForm({ ...form, intro_body: e.target.value })}
          />

          <div className="border-t pt-3">
            <div className="overline mb-2">SEO Meta</div>
            <div className="grid md:grid-cols-2 gap-3">
              <input
                className="kb-input"
                placeholder="Meta Title (<= 60 chars recommended)"
                value={form.meta_title}
                onChange={(e) =>
                  setForm({ ...form, meta_title: e.target.value })
                }
                maxLength={160}
              />
              <input
                className="kb-input"
                placeholder="Meta Keywords (comma separated)"
                value={keywordsStr}
                onChange={(e) => setKeywordsStr(e.target.value)}
              />
            </div>
            <textarea
              rows={2}
              className="kb-input mt-3"
              placeholder="Meta Description (<= 160 chars recommended)"
              value={form.meta_description}
              onChange={(e) =>
                setForm({ ...form, meta_description: e.target.value })
              }
              maxLength={320}
            />
          </div>

          <div>
            <label className="overline block mb-1">Sections (JSON array)</label>
            <textarea
              rows={10}
              className="kb-input font-mono text-xs"
              value={sectionsJson}
              onChange={(e) => setSectionsJson(e.target.value)}
              data-testid="campaign-sections"
            />
            <p className="text-xs text-[hsl(var(--kb-muted))] mt-1">
              Each item: <code>{"{ type: 'product_grid' | 'coupon_banner' | 'seo_block' | 'category_grid' | 'banner_strip' | 'brand_story' | 'reels_rail', title?, config: {} }"}</code>
            </p>
            <div className="flex gap-2 mt-2 flex-wrap">
              <button
                type="button"
                onClick={() => {
                  try {
                    const arr = JSON.parse(sectionsJson || "[]");
                    arr.push({
                      title: "Reels",
                      type: "reels_rail",
                      config: { heading: "Seen in reels", tag: "", limit: 8, cta_url: "/reels" },
                    });
                    setSectionsJson(JSON.stringify(arr, null, 2));
                  } catch {
                    /* leave it — invalid JSON will surface on save */
                  }
                }}
                className="kb-btn-outline text-xs"
                data-testid="campaign-add-reels-rail"
              >
                + Add Reels rail
              </button>
            </div>
          </div>

          <div className="grid md:grid-cols-4 gap-3">
            <input
              type="date"
              className="kb-input"
              value={form.start_at}
              onChange={(e) => setForm({ ...form, start_at: e.target.value })}
              data-testid="campaign-start-at"
            />
            <input
              type="date"
              className="kb-input"
              value={form.end_at}
              onChange={(e) => setForm({ ...form, end_at: e.target.value })}
              data-testid="campaign-end-at"
            />
            <input
              type="number"
              className="kb-input"
              placeholder="Order"
              value={form.order}
              onChange={(e) => setForm({ ...form, order: e.target.value })}
            />
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) =>
                  setForm({ ...form, is_active: e.target.checked })
                }
              />{" "}
              Active
            </label>
          </div>
        </div>
      </AdminDialog>
    </div>
  );
}
